import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { StandaloneCheckbox } from '@famly/mf_input_checkbox';
import { EmptyState } from '@famly/mf_data-display_empty-state';
import { Text } from '@famly/mf_data-display_text';
import { Stack } from '@famly/mf_layout_stack';
import { Box, createBox } from '@famly/mf_layout_box';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import ChildrenEntity from 'signin-app/entities/children';
import translate from 'signin-app/helpers/translate';
import * as GroupsSelectors from 'signin-app/groups/selectors';
import PersonButton from 'signin-app/components/person-button';
import Header from 'signin-app/components/header';
import * as PrivateRouteSelectors from 'signin-app/components/private-route/selectors';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import SignedInChildrenEmptyState from 'signin-app/resources/img/signed-in-children.svg';
import { StyledPageContainer } from 'signin-app/components/containers';
import { GroupGrid } from 'signin-app/components/group-grid';
import { Spinner } from '@famly/mf_feedback_spinner';
import { hasSignInRedesign } from 'signin-app/select-children/has-sign-in-redesign';
import { toggleWhoPicksUp } from './actions';
import { showSignedIn } from './selectors';
import { useElementDimensions, useGetPages } from './hooks';
import { SwipeableView } from './swipe-view';
const RoomChildCards = React.lazy(() => import('./room-child-cards').then(module => ({ default: module.RoomChildCards })));
const StyledEmptyStateContainer = styled(createBox({
    margin: '0 auto',
})) `
    p,
    h6 {
        color: ${props => props.theme.mf.colorPalette.n400};
    }
`;
const ShowOnlySignedInChildrenToggle = () => {
    const shouldShowPickupTimes = useTypedSelector(showSignedIn);
    const dispatch = useDispatch();
    const handleClick = React.useCallback(() => {
        dispatch(toggleWhoPicksUp);
    }, [dispatch]);
    return (_jsxs(Stack, { onClick: handleClick, backgroundColor: "n75", borderRadius: "12px", direction: "row", alignItems: "center", alignSelf: "flex-end", cursor: "pointer", pl: 4, pr: 2, py: 1, "data-e2e-id": "show-signed-in-toggle", children: [_jsx(Text, { variant: "body", color: "n400", children: translate('showPickupTimes') }), _jsx(StandaloneCheckbox, { size: "compact", checked: shouldShowPickupTimes })] }));
};
export const Group = (props) => {
    const childrenList = useTypedSelector(state => ChildrenEntity.selectors.getByGroupedId(state, { referenceId: props.match.params.groupId }));
    const { isTabletPortraitAndLarger } = useBreakpoints();
    const groupName = useTypedSelector(state => GroupsSelectors.getGroupName(state, { id: props.match.params.groupId }));
    const isFetching = useTypedSelector(PrivateRouteSelectors.isPrefetching);
    const shouldShowSignedIn = useTypedSelector(state => showSignedIn(state));
    const usesNewerScreens = useTypedSelector(hasSignInRedesign);
    const checkedInChildren = childrenList.filter(child => child.checkedIn);
    const childrenToRender = shouldShowSignedIn ? checkedInChildren : childrenList;
    const isEmptyState = useMemo(() => checkedInChildren.size === 0 && shouldShowSignedIn, [checkedInChildren, shouldShowSignedIn]);
    const { ref: swipeAreaRef, dimensions } = useElementDimensions();
    const { pages } = useGetPages({
        items: childrenToRender,
        width: dimensions.width,
        height: dimensions.height,
    });
    if (isFetching) {
        return (_jsx(Stack, { justifyContent: "center", marginY: 6, children: _jsx(Spinner, { size: 32, "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) }));
    }
    return (_jsxs(StyledPageContainer, { "data-e2e-id": "signin-child-room", children: [_jsx(Header, { centerElement: _jsx(Text, { flex: 1, variant: "h4", color: "n400", textAlign: { base: 'start', tabletPortrait: 'center' }, children: groupName }), 
                // On mobile we show the toggle in the page contents
                rightElement: isTabletPortraitAndLarger && _jsx(ShowOnlySignedInChildrenToggle, {}) }), _jsxs(Stack, { flexDirection: "column", mt: 6, gap: 8, height: "100%", children: [!isTabletPortraitAndLarger && (_jsx(Stack, { mb: 3, justifyContent: "end", children: _jsx(ShowOnlySignedInChildrenToggle, {}) })), !isEmptyState && usesNewerScreens ? (_jsx(React.Suspense, { fallback: null, children: _jsx(RoomChildCards, { roomChildren: childrenToRender }) })) : null, !isEmptyState && !usesNewerScreens ? (_jsx(Box, { ref: swipeAreaRef, height: "100%", children: _jsx(SwipeableView, { children: pages.map(({ items, pageId }) => (_jsx(GroupGrid, { children: items.map(child => {
                                    const childLink = child.checkedIn
                                        ? `/child/${child.id}/checkout`
                                        : `/child/${child.id}/checkin`;
                                    return (_jsx(PersonButton, { linkTo: childLink, person: child, hide: shouldShowSignedIn && !child.checkedIn ? true : undefined }, child.id));
                                }) }, pageId))) }, Date.now()) })) : null, isEmptyState ? (_jsx(Stack, { flexWrap: "wrap", gap: 4, "data-e2e-id": "signed-in-empty-state", children: _jsx(StyledEmptyStateContainer, { children: _jsx(EmptyState, { fullWidth: true, imageUrl: SignedInChildrenEmptyState, heading: translate('signedinChildrenEmptyStateHeading'), body: translate('signedinChildrenEmptyStateBody') }) }) })) : null] })] }));
};
